/**
 * TermsOfUse Component
 * Terms of Use page for HR4India / OKR Hub
 */

import React, { useEffect } from 'react';
import { LandingNav, Footer } from '../../components/landing';

export const TermsOfUse: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <LandingNav />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h1 className="text-4xl font-bold text-stone-900 mb-2">Terms of Use</h1>
          <p className="text-stone-500 mb-8">Last Updated: 08 Jan 2026</p>

          <div className="prose prose-stone max-w-none">
            <p className="text-stone-600 leading-relaxed mb-8">
              These Terms of Use ("Terms") govern access to and use of OKR Hub, a software platform operated by HR4India ("we", "our", "us").
            </p>
            <p className="text-stone-600 leading-relaxed mb-8">
              By accessing or using OKR Hub, you agree to be bound by these Terms. If you do not agree, you must not use the Services.
            </p>

            <Section title="1. About OKR Hub">
              <p>OKR Hub is a business-to-business (B2B) software platform designed to enable organizations to define, track, review, and manage Objectives and Key Results (OKRs) and related performance data.</p>
              <p>HR4India may provide OKR Hub as:</p>
              <ul>
                <li>Software-as-a-Service (SaaS)</li>
                <li>Managed services</li>
                <li>Consulting-led implementations</li>
              </ul>
            </Section>

            <Section title="2. Eligibility and Authority">
              <p>You may use OKR Hub only if:</p>
              <ul>
                <li>You are at least 18 years of age</li>
                <li>You are authorized to act on behalf of your organization</li>
                <li>Your use complies with applicable laws and these Terms</li>
              </ul>
              <p>If you access OKR Hub on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>
            </Section>

            <Section title="3. Account Registration and Access">
              <h4 className="font-semibold text-stone-800 mt-4 mb-2">3.1 Account Responsibility</h4>
              <ul>
                <li>You are responsible for maintaining the confidentiality of login credentials</li>
                <li>You are responsible for all activities conducted through your account</li>
                <li>You must notify us immediately of any unauthorized access or breach</li>
              </ul>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">3.2 Account Accuracy</h4>
              <p>You agree to provide accurate, current, and complete information during registration and usage.</p>
            </Section>

            <Section title="4. Acceptable Use">
              <p>You agree not to:</p>
              <ul>
                <li>Use the Services for unlawful, fraudulent, or misleading purposes</li>
                <li>Access or attempt to access data not intended for you</li>
                <li>Reverse engineer, copy, or resell the platform or its components</li>
                <li>Upload malicious code, viruses, or harmful scripts</li>
                <li>Interfere with system security, integrity, or performance</li>
                <li>Use the platform in a manner that violates employment, labor, or privacy laws</li>
              </ul>
              <p className="font-semibold text-stone-800 mt-4">HR4India reserves the right to suspend or terminate access for violations.</p>
            </Section>

            <Section title="5. Customer Data and Ownership">
              <h4 className="font-semibold text-stone-800 mt-4 mb-2">5.1 Data Ownership</h4>
              <p>All organizational data, OKRs, performance records, and content uploaded by customers ("Customer Data") remain the property of the customer organization.</p>
              <p className="font-semibold">HR4India does not claim ownership over Customer Data.</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">5.2 Data Processing</h4>
              <p>HR4India processes Customer Data solely for:</p>
              <ul>
                <li>Providing the Services</li>
                <li>Platform maintenance and improvement</li>
                <li>Support and security purposes</li>
              </ul>
              <p>Processing is governed by the Privacy Policy and, where applicable, a Data Processing Agreement (DPA).</p>
            </Section>

            <Section title="6. Intellectual Property Rights">
              <h4 className="font-semibold text-stone-800 mt-4 mb-2">6.1 HR4India IP</h4>
              <p>All rights, title, and interest in OKR Hub, including:</p>
              <ul>
                <li>Software</li>
                <li>Design</li>
                <li>Logos</li>
                <li>Documentation</li>
                <li>Methodologies</li>
              </ul>
              <p>are the exclusive property of HR4India.</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">6.2 Limited License</h4>
              <p>We grant you a limited, non-exclusive, non-transferable, revocable license to use OKR Hub solely for internal business purposes.</p>
            </Section>

            <Section title="7. Fees and Payment (If Applicable)">
              <p>Where Services are provided on a paid basis:</p>
              <ul>
                <li>Fees, billing cycles, and payment terms will be governed by a separate commercial agreement, proposal, or invoice</li>
                <li>Failure to pay may result in suspension or termination of access</li>
              </ul>
            </Section>

            <Section title="8. Service Availability and Modifications">
              <ul>
                <li>OKR Hub is provided on a commercially reasonable "as-is" and "as-available" basis</li>
                <li>We may modify, suspend, or discontinue features with reasonable notice</li>
                <li>We do not guarantee uninterrupted or error-free operation</li>
              </ul>
            </Section>

            <Section title="9. Third-Party Integrations">
              <p>OKR Hub may integrate with third-party tools (e.g., email, calendar, HR systems).</p>
              <p>HR4India is not responsible for:</p>
              <ul>
                <li>Third-party services</li>
                <li>Data handling by third parties</li>
                <li>Downtime or failures caused by third-party systems</li>
              </ul>
              <p>Use of third-party services is governed by their respective terms.</p>
            </Section>

            <Section title="10. Confidentiality">
              <p>Each party agrees to maintain the confidentiality of:</p>
              <ul>
                <li>Non-public business information</li>
                <li>Platform data</li>
                <li>Commercial terms</li>
                <li>Implementation details</li>
              </ul>
              <p className="font-semibold text-stone-800 mt-4">Confidentiality obligations survive termination of these Terms.</p>
            </Section>

            <Section title="11. Termination">
              <h4 className="font-semibold text-stone-800 mt-4 mb-2">11.1 Termination by User</h4>
              <p>You may discontinue use of the Services at any time, subject to contractual obligations.</p>

              <h4 className="font-semibold text-stone-800 mt-4 mb-2">11.2 Termination by HR4India</h4>
              <p>We may suspend or terminate access if:</p>
              <ul>
                <li>These Terms are violated</li>
                <li>There is misuse, security risk, or legal exposure</li>
                <li>Required by law or regulatory authority</li>
              </ul>
            </Section>

            <Section title="12. Disclaimer of Warranties">
              <p>OKR Hub is provided "as is" and "as available."</p>
              <p>To the fullest extent permitted by law, HR4India disclaims all warranties, including:</p>
              <ul>
                <li>Merchantability</li>
                <li>Fitness for a particular purpose</li>
                <li>Accuracy or completeness of results</li>
              </ul>
              <p className="font-semibold text-stone-800 mt-4">OKR Hub is a decision-support tool and does not replace managerial judgment or legal, financial, or HR advice.</p>
            </Section>

            <Section title="13. Limitation of Liability">
              <p>To the maximum extent permitted by law:</p>
              <ul>
                <li>HR4India shall not be liable for indirect, incidental, consequential, or punitive damages</li>
                <li>Total liability shall not exceed the fees paid for the Services in the preceding 12 months</li>
              </ul>
            </Section>

            <Section title="14. Indemnification">
              <p>You agree to indemnify and hold harmless HR4India from claims arising from:</p>
              <ul>
                <li>Misuse of the Services</li>
                <li>Violation of these Terms</li>
                <li>Violation of applicable laws</li>
                <li>Unauthorized use of Customer Data</li>
              </ul>
            </Section>

            <Section title="15. Governing Law and Jurisdiction">
              <p>These Terms shall be governed by and construed in accordance with the laws of India.</p>
              <p>All disputes arising out of or relating to these Terms or the Services shall be subject to the exclusive jurisdiction of the courts of Mumbai, Maharashtra, India.</p>
            </Section>

            <Section title="16. Amendments">
              <p>HR4India may update these Terms from time to time.</p>
              <p>Material changes will be communicated via the platform or email.</p>
              <p>Continued use of the Services constitutes acceptance of the updated Terms.</p>
            </Section>

            <Section title="17. Contact Information">
              <p>For questions regarding these Terms, contact:</p>
              <div className="bg-stone-50 p-4 rounded-lg mt-4">
                <p className="font-semibold text-stone-800">HR4India</p>
                <p>Email: <a href="mailto:okrhub@hr4india.com" className="text-primary-600 hover:text-primary-700">okrhub@hr4india.com</a></p>
                <p>Address: A502, Dheeraj Ganga, Chincholi Bunder Road, Malad West, Mumbai 400064, MH-India</p>
              </div>
            </Section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-stone-900 mb-4">{title}</h2>
    <div className="text-stone-600 leading-relaxed space-y-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-1">
      {children}
    </div>
  </section>
);
